import { useEffect } from 'react'

import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import Cookies from 'js-cookie'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  UpdateUserRequest,
  UpdateUserResponse,
  UploadCandidatePictureRequest,
} from '@pkg/types/api'

import api from '@/api'
import { cleanFilters } from '@/views/screens/Home/FilterHeader/const'

export const DATABASE_USER_KEY = 'databaseUser'
export const IS_EMAIL_TEMPORARY_KEY = 'isEmailTemporary'
export const USER_BILLING_INFO_KEY = 'userBillingInfo'

export const useUser = () => {
  const user = getAuth().currentUser

  return useQuery({
    queryKey: [DATABASE_USER_KEY],
    queryFn: async () => {
      const res = await api.getUser()
      return res.data
    },
    enabled: !!user, // Only run the query if the user is signed in
  })
}

export const useUserBillingInfo = () => {
  const user = getAuth().currentUser

  return useQuery({
    queryKey: [USER_BILLING_INFO_KEY],
    queryFn: async () => {
      const res = await api.getUserBillingInfo()
      return res.data.userBillingInfo
    },
    enabled: !!user,
  })
}

export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const user = getAuth().currentUser
      if (!user?.uid || !user.email || !user.displayName) {
        throw new Error('Missing user data')
      }

      const databaseUser = (
        await api.createUser({
          user: {
            firebaseId: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            displayName: user.displayName,
            photoURL: user.photoURL || undefined,
            phoneNumber: user.phoneNumber || undefined,
          },
          referrerCode: Cookies.get('referralCode') || undefined,
        })
      ).data.user

      Cookies.remove('referralCode')

      return databaseUser
    },
    onSuccess: (data) => {
      queryClient.setQueryData([DATABASE_USER_KEY], data)
    },
  })
}

export const useDeleteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await api.deleteUser()
      await signOut(getAuth())
    },
    onSuccess: () => {
      // invalidate all queries
      queryClient.clear()
    },
  })
}

export const useSignOut = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await signOut(getAuth())
    },
    onSuccess: () => {
      queryClient.clear()
      cleanFilters()
    },
  })
}

export const useUpdateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateUserRequest) => {
      return await api.updateUser(data)
    },
    onSuccess: (data) => {
      queryClient.setQueryData([DATABASE_USER_KEY], data.data.user)
    },
  })
}

export const useIsEmailTemporary = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      return await api.isEmailTemporary({ email })
    },
    mutationKey: [IS_EMAIL_TEMPORARY_KEY],
  })
}

export const useAuthListener = () => {
  const queryClient = useQueryClient()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      if (user) {
        // User is signed in, invalidate to refetch user data
        queryClient.invalidateQueries({
          queryKey: [DATABASE_USER_KEY],
        })
      } else {
        // User is signed out, remove user data from cache
        queryClient.removeQueries({
          queryKey: [DATABASE_USER_KEY],
        })
      }
    })

    return () => {
      unsubscribe()
    }
  }, [queryClient])
}

export const useUploadUserPicture = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UploadCandidatePictureRequest) => {
      const response = await api.uploadUserPicture(data)
      return response.data.pictureUrl
    },
    onSuccess: (pictureUrl) => {
      queryClient.setQueryData(
        [DATABASE_USER_KEY],
        (oldUser: UpdateUserResponse['user']) => ({
          ...oldUser,
          photoURL: pictureUrl,
        }),
      )
    },
  })
}
